import { createWorld, type Entity, type World } from 'koota';
import { MAX_PLAYERS } from '../core/players';
import { matchLevelPool } from '../levels/catalog';
import { World as PhysicsWorld } from 'planck';
import { thinkBots } from './ai/bots';
import { bindContext, getContext, makeContext, type SimContext } from './context';
import type { SimEvents } from './events';
import { blankInputs, normalizeInput, type PlayerInput } from './input';
import { loadLevel, spawnPlayer } from './level/loader';
import type { LevelDef } from './level/schema';
import { spawnPositions } from './level/spawns';
import { assignNetId, createBoxBody, registerBody } from './physics/bodies';
import type { FixtureUserData } from './physics/categories';
import { cloneTuning } from './tuning';
import { mergeSettings, type MatchSettings } from './rules/settings';
import { hashWorld, serializeWorld } from './snapshot';
import { combat } from './player/combat';
import { controller } from './player/controller';
import { rules } from './rules/rounds';
import { updateKillStreaks } from './rules/killStreaks';
import { spawner } from './rules/spawner';
import { applyInputs } from './systems/applyInputs';
import { cleanup } from './systems/cleanup';
import { damageDeath } from './systems/damage';
import { hazardsStep } from './systems/hazards';
import { physicsStep, syncTransforms } from './systems/syncTransforms';
import { projectiles } from './weapons/projectiles';
import { weapons } from './weapons/systems';
import {
  DropState,
  MatchState,
  PartyState,
  PrevTransform,
  RoundPhase,
  RoundState,
  SimClock,
  Transform,
} from './traits';

export type CreateSimOptions = {
  level: LevelDef;
  seed: number;
  settings?: Partial<MatchSettings>;
  spawnPlayers?: boolean;
  boxes?: number;
  extraLevels?: LevelDef[];
};

export type SimHandle = {
  ecs: World;
  ctx: SimContext;
  step: (inputs?: PlayerInput[]) => SimEvents;
  getTick: () => number;
  hash: () => string;
  snapshot: () => ReturnType<typeof serializeWorld>;
  players: () => Entity[];
  /** Release the ECS world slot (Koota caps live worlds); the handle is unusable afterwards. */
  destroy: () => void;
};

export function createSimWorld(opts: CreateSimOptions): SimHandle {
  const settings = mergeSettings(opts.settings);
  const count = Math.max(1, Math.min(MAX_PLAYERS, settings.playerCount + settings.bots));
  const level =
    settings.mode === 'office-brawl' || (opts.spawnPlayers !== false && opts.level.spawns.length < count)
      ? matchLevelPool(settings.enabledLevels, opts.extraLevels ?? [], count, settings.mode)[0]!
      : opts.level;
  const ecs = createWorld();
  const g = cloneTuning().gravity;
  const physics = new PhysicsWorld({ gravity: { x: 0, y: -g } });
  const ctx = makeContext(ecs, physics, level, opts.seed, settings);
  ctx.extraLevels = opts.extraLevels ?? [];
  if (settings.mutators.includes('low-gravity')) {
    ctx.tuning.gravity *= 0.55;
    physics.setGravity({ x: 0, y: -ctx.tuning.gravity });
  }
  if (settings.mutators.includes('giant-knockback')) {
    ctx.tuning.punchKnockback *= 2.2;
    ctx.tuning.punchKnockbackUp *= 2.2;
  }
  bindContext(ecs, ctx);
  physics.on('begin-contact', (contact) => {
    const a = contact.getFixtureA().getBody().getUserData() as FixtureUserData | undefined;
    const b = contact.getFixtureB().getBody().getUserData() as FixtureUserData | undefined;
    if (a?.kind === 'projectile') ctx.contactHits.add(a.entity);
    if (b?.kind === 'projectile') ctx.contactHits.add(b.entity);
  });

  ecs.add(
    RoundState({
      phase: RoundPhase.Loading,
      ticks: 0,
      aliveMask: 0,
      lastKiller: -1,
      seed: opts.seed,
    }),
    MatchState({
      wins0: 0,
      wins1: 0,
      wins2: 0,
      wins3: 0,
      firstTo: settings.firstTo,
      levelIndex: 0,
      rotation: settings.rotation === 'ordered' ? 1 : 0,
      showWins: settings.showWins ? 1 : 0,
      maxHp: settings.maxHp,
      round: 0,
    }),
    SimClock({ tick: 0, stepScale: 1 }),
    PartyState(),
    DropState({ nextDrop: 0, looseCount: 0, wave: 0, waveSize: 0 }),
  );

  loadLevel(ecs, level);

  if (opts.spawnPlayers !== false) {
    const spots = spawnPositions(level.spawns, count, ctx.rng);
    for (let i = 0; i < count; i++) {
      const spawn = spots[i]!;
      spawnPlayer(ecs, i, spawn.x, spawn.y, settings.colors?.[i] ?? i, i);
    }
  }

  if (opts.boxes && opts.boxes > 0) {
    spawnTestBoxes(ecs, opts.boxes);
  }

  const step = (inputs?: PlayerInput[]): SimEvents => {
    ctx.events = [];
    ctx.contactHits.clear();
    ctx.prevInputs = ctx.inputs.map((i) => ({ ...i }));
    const incoming = inputs ?? blankInputs();
    ctx.rawInputs = incoming.map((i) => ({ ...i }));
    ctx.inputs = incoming.map(normalizeInput);
    thinkBots(ecs);
    applyInputs(ecs);
    controller(ecs);
    combat(ecs);
    weapons(ecs);
    projectiles(ecs);
    hazardsStep(ecs);
    physicsStep(ecs);
    syncTransforms(ecs);
    damageDeath(ecs);
    updateKillStreaks(ecs);
    rules(ecs);
    spawner(ecs);
    cleanup(ecs);
    ctx.tick += 1;
    ecs.set(SimClock, { tick: ctx.tick, stepScale: 1 });
    return ctx.events;
  };

  return {
    ecs,
    ctx,
    step,
    getTick: () => ctx.tick,
    hash: () => hashWorld(ecs),
    snapshot: () => serializeWorld(ecs),
    players: () => ctx.players.slice(),
    destroy: () => {
      ecs.destroy();
    },
  };
}

function spawnTestBoxes(world: World, count: number): void {
  const ctx = getContext(world);
  for (let i = 0; i < count; i++) {
    const x = 8 + (i % 6) * 1.2;
    const y = 10 + Math.floor(i / 6) * 1.2;
    const e = world.spawn(Transform({ x, y, angle: 0 }), PrevTransform({ x, y, angle: 0 }));
    assignNetId(world, e);
    const body = createBoxBody(ctx.physics, e, 'prop', x, y, 0.4, 0.4, 'dynamic', {
      density: 1,
      friction: 0.4,
      restitution: 0.05,
      fixedRotation: false,
    });
    registerBody(world, e, body);
  }
}
